import type { ReactElement } from 'react'

import { FlowPage } from '@monthio/design-system'
import { useTranslation } from 'react-i18next'

import type { QuestionnaireStepKey } from '../budgetHelperStateMachine'
import type { StepProps } from './Step'

import { BudgetFlowStartLandingPage } from './BudgetFlowStartLandingPage'
import { ChildrenAgesStep } from './ChildrenAgesStep'
import { ChildrenSelectorStep } from './ChildrenSelectorStep'
import { Step } from './Step'
import { Alternative, StepMultipleChoice } from './StepMultipleChoice'

export enum StepKey {
	Cohabiting = 'civilStatus',
	MergedFinances = 'mergedFinances',
	SpouseConsent = 'spouseConsent',
	ChildrenSelector = 'childrenSelector',
	ChildrenAgesSelector = 'childrenAges',
	BudgetFlowStart = 'budgetFlowStart',
	End = 'end',
}

export type StepComponent = (props: StepProps) => ReactElement

type StepsType = {
	[key in QuestionnaireStepKey]: StepComponent
}

export enum CivilStatus {
	None = 'none',
	Married = 'married',
	Alone = 'alone',
}

const CohabitingStep: StepComponent = ({ handleClick, formProps }) => {
	const { t } = useTranslation()

	return (
		<FlowPage.Content $spacing="xs">
			<FlowPage.Header title={t('budgetHelper-cohabiting-title')} />
			<Step
				description="budgetHelper-cohabiting-description"
				invalidReason="budgetHelper-cohabiting-invalidReason"
			>
				{() => (
					<StepMultipleChoice
						id={StepKey.Cohabiting}
						choices={[
							{
								value: CivilStatus.Married,
								label: t('btn-yes'),
							},
							{
								value: CivilStatus.Alone,
								label: t('btn-no'),
							},
						]}
						handleClick={handleClick}
						formProps={formProps}
					/>
				)}
			</Step>
		</FlowPage.Content>
	)
}

const MergedFinancesStep: StepComponent = ({ handleClick, formProps }) => {
	const { t } = useTranslation()

	return (
		<FlowPage>
			<FlowPage.Content $spacing="l">
				<FlowPage.Header title={t('budgetHelper-mergedFinances-title')} />
				<Step description="budgetHelper-mergedFinances-description">
					{() => (
						<StepMultipleChoice
							id={StepKey.MergedFinances}
							handleClick={handleClick}
							formProps={formProps}
						/>
					)}
				</Step>
			</FlowPage.Content>
		</FlowPage>
	)
}

const SpouseConsentStep: StepComponent = ({ handleClick, formProps }) => {
	const { t } = useTranslation()

	return (
		<FlowPage>
			<FlowPage.Content $spacing="l">
				<FlowPage.Header title={t('budgetHelper-spouseConsent-title')} />
				<Step
					description="budgetHelper-spouseConsent-description"
					invalidReason="budgetHelper-spouseConsent-invalidReason"
				>
					{({ setIsInvalid }) => (
						<StepMultipleChoice
							id={StepKey.SpouseConsent}
							invalidAnswer={Alternative.NO}
							setIsInvalid={setIsInvalid}
							handleClick={handleClick}
							formProps={formProps}
						/>
					)}
				</Step>
			</FlowPage.Content>
		</FlowPage>
	)
}

export const steps: StepsType = {
	[StepKey.Cohabiting]: CohabitingStep,
	[StepKey.MergedFinances]: MergedFinancesStep,
	[StepKey.SpouseConsent]: SpouseConsentStep,
	[StepKey.ChildrenSelector]: ChildrenSelectorStep,
	[StepKey.ChildrenAgesSelector]: ChildrenAgesStep,
	[StepKey.BudgetFlowStart]: BudgetFlowStartLandingPage,
}
